<div
  class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink/80 px-4 py-10"
  (click)="close()"
>
  <div
    class="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-md border border-hairline bg-panel p-6"
    (click)="$event.stopPropagation()"
  >
    <div class="mb-4 flex items-start justify-between gap-4">
      <h2 class="font-display text-xl font-semibold text-parchment">
        {{ displayName() }}'s entries
      </h2>
      <button
        type="button"
        (click)="close()"
        class="text-xl leading-none text-dusk hover:text-parchment"
        aria-label="Close"
      >
        &times;
      </button>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto">
    @if (loading()) {
      <p class="text-dusk">Loading…</p>
    } @else if (errorMessage()) {
      <p class="rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
        {{ errorMessage() }}
      </p>
    } @else {
      <app-pick-list
        [picks]="picks()"
        [allowRemove]="isSiteAdmin()"
        [removingId]="removingId()"
        (remove)="removePick($event)"
      />

      @if (isSiteAdmin()) {
        <div class="mt-4 border-t border-hairline pt-4">
          <p class="mb-2 text-xs uppercase tracking-wide text-dusk">
            Add a pick (site admin — includes already-deceased people, for recreating historical
            entries)
          </p>
          <form [formGroup]="searchForm" (ngSubmit)="search()" class="flex gap-2">
            <input
              formControlName="q"
              type="text"
              placeholder="Search for a celebrity…"
              class="flex-1 rounded border border-hairline bg-ink px-3 py-2 text-sm text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
            />
            <button
              type="submit"
              [disabled]="searching()"
              class="inline-flex shrink-0 items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-4 py-2 text-sm font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
            >
              {{ searching() ? 'Searching…' : 'Search' }}
            </button>
          </form>

          @if (searchResults().length > 0) {
            <ng-template #resultRow let-result>
              <li class="flex items-center justify-between gap-3 rounded p-2 text-sm">
                <span class="flex items-center gap-2 text-parchment">
                  @if (result.imageUrl) {
                    <img [src]="result.imageUrl" alt="" class="h-8 w-8 shrink-0 rounded-full object-cover" />
                  }
                  {{ result.name }}{{ result.aliases?.length ? ' "' + result.aliases.join('", "') + '"' : '' }}{{ result.knownFor ? ' (' + result.knownFor + ')' : '' }}
                  @if (result.status === 'DEAD') {
                    <span class="text-xs font-semibold text-wax-bright">Deceased</span>
                  }
                </span>
                <span class="flex shrink-0 gap-1">
                  <button
                    type="button"
                    (click)="addPick(result, false)"
                    [disabled]="regularCapReached() || addingKey() === resultKey(result, false)"
                    class="rounded-[3px] border border-hairline px-2 py-1 text-xs text-parchment transition hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Regular
                  </button>
                  <button
                    type="button"
                    (click)="addPick(result, true)"
                    [disabled]="alternateCapReached() || addingKey() === resultKey(result, true)"
                    class="rounded-[3px] border border-hairline px-2 py-1 text-xs text-parchment transition hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Alternate
                  </button>
                </span>
              </li>
            </ng-template>

            <ul class="mt-3 flex flex-col gap-1 border-t border-hairline pt-3">
              @for (result of visibleResults(); track resultKey(result, false)) {
                <ng-container [ngTemplateOutlet]="resultRow" [ngTemplateOutletContext]="{ $implicit: result }" />
              }

              @if (hiddenResults().length > 0) {
                <button
                  type="button"
                  (click)="showHidden.set(!showHidden())"
                  class="rounded px-2 py-1 text-left text-xs text-dusk transition hover:bg-parchment/[0.04] hover:text-parchment hover:underline"
                >
                  {{ showHidden() ? 'Hide' : 'Show ' + hiddenResults().length + ' Hidden' }}
                </button>
                @if (showHidden()) {
                  @for (result of hiddenResults(); track resultKey(result, false)) {
                    <ng-container [ngTemplateOutlet]="resultRow" [ngTemplateOutletContext]="{ $implicit: result }" />
                  }
                }
              }
            </ul>
          }

          @if (visibleResults().length > 0 && !wikipediaAlsoSearched()) {
            <button
              type="button"
              (click)="search(true)"
              [disabled]="searching()"
              class="mt-2 rounded px-2 py-1 text-left text-xs text-dusk transition hover:bg-parchment/[0.04] hover:text-parchment hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Not the right person? Search Wikipedia
            </button>
          }

          @if (noResultsFound()) {
            <p class="mt-3 text-sm text-dusk">No results found.</p>
          }

          @if (searchError()) {
            <p class="mt-3 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
              {{ searchError() }}
            </p>
          }

          <button
            type="button"
            (click)="toggleBulkAdd()"
            class="mt-3 text-xs text-dusk hover:text-parchment hover:underline"
          >
            {{ showBulkAdd() ? 'Hide bulk add' : 'Bulk add by name (comma-separated)' }}
          </button>

          @if (showBulkAdd()) {
            <div class="mt-2 rounded border border-hairline p-3">
              <p class="mb-2 text-xs text-dusk">
                Paste names already in the catalog, separated by commas — this only searches
                locally, so nothing new gets created. An exact match offers Regular/Alternate
                directly; more than one match lets you pick which person.
              </p>
              <form [formGroup]="bulkNamesForm" (ngSubmit)="bulkLookUp()" class="flex flex-col gap-2">
                <textarea
                  formControlName="names"
                  rows="3"
                  placeholder="Brad Arnold, James Van Der Beek, ..."
                  class="rounded border border-hairline bg-ink px-3 py-2 text-sm text-parchment placeholder:text-dusk/60 focus:border-gold focus:outline-none"
                ></textarea>
                <button
                  type="submit"
                  [disabled]="bulkLookingUp()"
                  class="inline-flex shrink-0 items-center justify-center self-start rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-4 py-1.5 text-xs font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {{ bulkLookingUp() ? 'Looking up…' : 'Look up' }}
                </button>
              </form>

              @if (bulkLookupError()) {
                <p class="mt-2 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
                  {{ bulkLookupError() }}
                </p>
              }

              @if (bulkRows().length > 0) {
                <ng-template #bulkCandidateRow let-result let-row="row">
                  <li class="flex items-center justify-between gap-3 rounded p-2 text-sm">
                    <span class="flex items-center gap-2 text-parchment">
                      @if (result.imageUrl) {
                        <img [src]="result.imageUrl" alt="" class="h-7 w-7 shrink-0 rounded-full object-cover" />
                      }
                      {{ result.name }}{{ result.aliases?.length ? ' "' + result.aliases.join('", "') + '"' : '' }}{{ result.knownFor ? ' (' + result.knownFor + ')' : '' }}
                      @if (result.status === 'DEAD') {
                        <span class="text-xs font-semibold text-wax-bright">Deceased</span>
                      }
                    </span>
                    <span class="flex shrink-0 gap-1">
                      <button
                        type="button"
                        (click)="addBulkPick(row, result, false)"
                        [disabled]="regularCapReached() || row.status === 'adding'"
                        class="rounded-[3px] border border-hairline px-2 py-1 text-xs text-parchment transition hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Regular
                      </button>
                      <button
                        type="button"
                        (click)="addBulkPick(row, result, true)"
                        [disabled]="alternateCapReached() || row.status === 'adding'"
                        class="rounded-[3px] border border-hairline px-2 py-1 text-xs text-parchment transition hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Alternate
                      </button>
                    </span>
                  </li>
                </ng-template>

                <ul class="mt-3 flex flex-col gap-2 border-t border-hairline pt-3">
                  @for (row of bulkRows(); track row.name) {
                    <li>
                      <p class="text-xs font-semibold uppercase tracking-wide text-dusk">
                        {{ row.name }}
                        @if (row.status === 'added') {
                          <span class="text-gold">— Added</span>
                        } @else if (row.matches.length === 0) {
                          <span>— not found</span>
                        } @else if (row.matches.length > 1) {
                          <span>— {{ row.matches.length }} matches, pick one</span>
                        }
                      </p>
                      @if (row.status === 'error') {
                        <p class="text-xs text-wax-bright">{{ row.errorMessage }}</p>
                      }
                      @if (row.status !== 'added' && row.matches.length > 0) {
                        <ul class="mt-1 flex flex-col gap-1">
                          @for (result of row.matches; track bulkResultKey(result)) {
                            <ng-container
                              [ngTemplateOutlet]="bulkCandidateRow"
                              [ngTemplateOutletContext]="{ $implicit: result, row: row }"
                            />
                          }
                        </ul>
                      }
                    </li>
                  }
                </ul>
              }
            </div>
          }
        </div>
      }
    }
    </div>
  </div>
</div>
